import type {
  CopilotProposalId,
  CopilotProposalResourceType,
} from "@repo/app-types/entities/copilotProposal";
import { IconArrowRight, IconChevronDown } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";

export interface CopilotProposalListRow {
  /** The proposal's id, which is also what opening the row navigates to. */
  id: CopilotProposalId;
  /** What kind of resource the proposal configures; titles the list. */
  resourceType: CopilotProposalResourceType;
  name: string;
  /**
   * Where the proposal ended up. Only a pending row opens — the rest are the
   * record of what became of it: your Apply / Discard decision, or `stale`
   * because a different proposal on the same node was applied.
   */
  status: "pending" | "applied" | "discarded" | "stale";
  /**
   * What the proposal targets, shown under the name. Set only where the list
   * spans more than one, since naming the same playbook on every row is noise.
   */
  resourceName?: string;
}

export interface CopilotProposalListProps {
  rows: readonly CopilotProposalListRow[];
  onSelect: (row: CopilotProposalListRow) => void;
  className?: string;
}

const LIST_TITLES: Record<CopilotProposalResourceType, string> = {
  playbook: "Playbook proposals",
  signal: "Signal proposals",
};

/** Named for the one resource type the rows share; generic when they mix. */
function buildListTitle(rows: readonly CopilotProposalListRow[]): string {
  const resourceTypes = new Set(rows.map((row) => row.resourceType));
  const [onlyResourceType] = resourceTypes;
  return resourceTypes.size === 1 && onlyResourceType
    ? LIST_TITLES[onlyResourceType]
    : "Proposals";
}

/**
 * The proposals from one chat turn, rendered inline beneath the turn's
 * message. Collapsible so a long list doesn't crowd the transcript.
 */
export const CopilotProposalList: FC<CopilotProposalListProps> = ({
  rows,
  onSelect,
  className,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (rows.length === 0) {
    return null;
  }

  return (
    <div
      className={twMerge(
        "w-full min-w-0 overflow-hidden rounded-lg border border-border-light bg-white shadow-xs",
        className,
      )}
    >
      <div className="flex min-w-0 items-center justify-between gap-2 px-3 py-1.5">
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground">
          {buildListTitle(rows)}
        </span>
        <button
          type="button"
          onClick={() => {
            setIsCollapsed((prev) => !prev);
          }}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? "Expand proposals" : "Collapse proposals"}
          className="-mr-1 flex size-5 shrink-0 items-center justify-center rounded-sm text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <IconChevronDown
            className={twMerge(
              "size-4 transition-transform duration-200 ease-out",
              isCollapsed && "-rotate-90",
            )}
          />
        </button>
      </div>
      <div
        className={twMerge(
          "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
          isCollapsed
            ? "grid-rows-[0fr] opacity-0"
            : "grid-rows-[1fr] opacity-100",
        )}
      >
        <ul className="flex min-w-0 flex-col overflow-hidden border-t border-gray-100">
          {rows.map((row, index) => (
            <li key={row.id} className="min-w-0">
              <button
                type="button"
                disabled={row.status !== "pending"}
                onClick={() => {
                  onSelect(row);
                }}
                className={twMerge(
                  "group flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600",
                  row.status === "pending"
                    ? "text-foreground hover:bg-gray-50"
                    : "cursor-default text-muted-foreground",
                  index > 0 && "border-t border-gray-100",
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate" title={row.name}>
                    {row.name}
                  </span>
                  {row.resourceName ? (
                    <span className="block truncate text-xs text-muted-foreground">
                      {row.resourceName}
                    </span>
                  ) : null}
                </span>
                {row.status === "pending" ? (
                  <IconArrowRight className="size-4 shrink-0 text-gray-400 group-hover:text-gray-600" />
                ) : (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {row.status === "applied"
                      ? "Accepted"
                      : row.status === "discarded"
                        ? "Discarded"
                        : "Outdated"}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
